import type {
   ApalumaChatMessage,
   ApalumaChatThread,
   ApalumaChatThreadsResponse,
   ChatMessageResponse,
   ChatType,
} from '@/types/api-response/chat-session-types';
import { useApiFetch } from '@/services/api-client';

/* --------------------------------- Threads -------------------------------- */

export interface ListThreadsParams {
   chat_type?: ChatType;
   project_id?: string;
   keyword?: string;
   limit?: number;
   offset?: number;
}

export function listThreads(
   params: ListThreadsParams = {},
   signal?: AbortSignal,
): Promise<ApalumaChatThreadsResponse> {
   return useApiFetch<ApalumaChatThreadsResponse>('/chat/threads', {
      method: 'GET',
      signal,
      query: params,
   });
}

export interface CreateThreadBody {
   chat_type?: ChatType;
   document_id?: string;
}

export function createThread(body: CreateThreadBody = {}): Promise<ApalumaChatThread> {
   return useApiFetch<ApalumaChatThread>('/chat/threads', {
      method: 'POST',
      body,
   });
}

export function createProjectThread(projectId: string): Promise<ApalumaChatThread> {
   return useApiFetch<ApalumaChatThread>(`/projects/${projectId}/threads`, {
      method: 'POST',
   });
}

export function getThread(
   threadId: number,
   params?: { limit?: number; offset?: number },
   signal?: AbortSignal,
): Promise<ApalumaChatThread & { messages: ApalumaChatMessage[] }> {
   return useApiFetch<ApalumaChatThread & { messages: ApalumaChatMessage[] }>(
      `/chat/threads/${threadId}`,
      {
         signal,
         query: params,
      },
   );
}

export interface UpdateThreadBody {
   title?: string;
   is_archived?: boolean;
}

export function updateThread(
   threadId: number,
   body: UpdateThreadBody,
): Promise<ApalumaChatThread> {
   return useApiFetch<ApalumaChatThread>(`/chat/threads/${threadId}`, {
      method: 'PUT',
      body,
   });
}

export function deleteThread(threadId: number): Promise<ApalumaChatThread> {
   return useApiFetch<ApalumaChatThread>(`/chat/threads/${threadId}`, {
      method: 'DELETE',
   });
}

/* -------------------------------- Messages -------------------------------- */

export interface SendMessageBody {
   content: string;
   role: 'user' | 'assistant';
   metadata?: Record<string, unknown>;
}

export function sendMessage(
   threadId: number,
   body: SendMessageBody,
   signal?: AbortSignal,
): Promise<ChatMessageResponse> {
   return useApiFetch<ChatMessageResponse>(
      `/chat/threads/${threadId}/messages`,
      {
         method: 'POST',
         signal,
         body,
      },
   );
}

export function saveAssistantMessage(
   threadId: number,
   content: string,
   reference?: Record<string, unknown>[],
   filter?: Record<string, unknown>[],
): Promise<void> {
   return useApiFetch(`/chat/threads/${threadId}/messages`, {
      method: 'POST',
      body: {
         content,
         role: 'assistant',
         ...(reference && { reference }),
         ...(filter && { filter }),
      },
   });
}
